import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, AlertCircle, Home, MapPin, BadgeCheck, Building2, Map } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ValidationModal } from '@/components/ui/ValidationModal'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { SectionCard } from '@/components/ui/SectionCard'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import styles from '@/features/company/styles/companyAdd.module.css'
import tabStyles from '@/features/company/styles/companyInfoTab.module.css'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { getStates, type State } from '@/lib/api/statesService'
import { getCompaniesForAddProperty, createProperty, buildPropertyFormData } from '../services/propertyService'
import { propertyAddSchema } from '../utils/propertyValidators'
import { ADDRESS_WARNING } from '../constants'
import type { PropertyAddFormValues } from '../types'
import { loadGoogleMapsScript } from '@/lib/utils/googleMaps'
import { maskPhoneOnChange, PROPERTY_PHONE_INPUT_MAX_LENGTH } from '../utils/formatPhone'
import { geocodeAddressRaw, matchZipAndStateAngularStyle } from '@/lib/utils/geocoding'
import { formatApiError, showSuccess } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import { parseDuplicateAddressError } from '../utils/extractDuplicateAddressError'
import { collectNestedErrorMessages } from '@/features/config/utils/formErrors'
import { cn } from '@lib/utils/helpers'
import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'

function toTitleCase(str: string): string {
  if (!str) return ''
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  )
}

function stripPhoneDigits(phone: string | undefined): string {
  if (!phone) return ''
  return phone.replace(/\D/g, '')
}

export function PropertyAdd() {
  const navigate = useNavigate()
  const { isLibertyRole, companyId: userCompanyId, companyName: userCompanyName } = useUserRole()
  const [isCompanyReadonly, setIsCompanyReadonly] = useState(false)
  const [states, setStates] = useState<State[]>([])
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [messagePopup, setMessagePopup] = useState<{
    isOpen: boolean
    title: string
    message: string
    variant?: 'error' | 'success'
    alignMessage?: boolean
    okButtonStyle?: 'primary' | 'saveChanges'
  }>({ isOpen: false, title: '', message: '' })
  const [addressValidated, setAddressValidated] = useState(false)
  const [isValidatingAddress, setIsValidatingAddress] = useState(false)
  const [mapMessage, setMapMessage] = useState('')
  const [duplicateAddressConfirm, setDuplicateAddressConfirm] = useState<{
    isOpen: boolean
    propertyLinks: string
  }>({ isOpen: false, propertyLinks: '' })
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<google.maps.Map | null>(null)
  const markerInstance = useRef<google.maps.Marker | null>(null)

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<PropertyAddFormValues>({
    resolver: zodResolver(propertyAddSchema, undefined, { mode: 'sync' }),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      fk_company_id: '',
      property_name: '',
      contact_name: '',
      contact_phone: '',
      property_office_phone: '',
      property_address: '',
      property_address2: '',
      property_city: '',
      property_state: '',
      property_zipcode: '',
      units_count: '',
      listed: '1',
      latitude: '30.5557898',
      longitude: '-87.8939053',
    },
  })

  const watchLat = watch('latitude')
  const watchLng = watch('longitude')
  const watchAddress = watch('property_address')
  const watchAddress2 = watch('property_address2')
  const watchCity = watch('property_city')
  const watchState = watch('property_state')
  const watchZip = watch('property_zipcode')

  useEffect(() => {
    if (addressValidated) setAddressValidated(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchAddress, watchAddress2, watchCity, watchState, watchZip])

  useEffect(() => {
    getStates().then(setStates).catch(console.error)
  }, [])

  useEffect(() => {
    if (userCompanyId) {
      setIsCompanyReadonly(true)
      setCompanies([{ value: String(userCompanyId), label: userCompanyName || String(userCompanyId) }])
      setValue('fk_company_id', String(userCompanyId))
    } else {
      loadCompanies()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLibertyRole, userCompanyId, userCompanyName, setValue])

  useEffect(() => {
    let cancelled = false
    const lat = Number(watchLat) || 30.5557898
    const lng = Number(watchLng) || -87.8939053

    const timer = setTimeout(async () => {
      if (!mapRef.current || cancelled) return
      const el = mapRef.current
      const rect = el.getBoundingClientRect()
      if (rect.width < 10 || rect.height < 10) return
      try {
        await loadGoogleMapsScript()
        if (cancelled || !window.google?.maps) return
        if (!mapRef.current) return
        if (!mapInstance.current) {
          mapInstance.current = new window.google.maps.Map(mapRef.current, {
            center: { lat, lng },
            zoom: 16,
          })
          markerInstance.current = new window.google.maps.Marker({
            position: { lat, lng },
            map: mapInstance.current,
          })
          const triggerResize = () => {
            if (mapInstance.current && !cancelled) {
              window.google.maps.event.trigger(mapInstance.current, 'resize')
              mapInstance.current.setCenter({ lat, lng })
            }
          }
          window.google.maps.event.addListenerOnce(mapInstance.current, 'idle', triggerResize)
          setTimeout(triggerResize, 250)
        } else if (mapInstance.current && markerInstance.current) {
          mapInstance.current.setCenter({ lat, lng })
          markerInstance.current.setPosition({ lat, lng })
        }
      } catch (e) {
        console.error('Map init error', e)
      }
    }, 400)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [watchLat, watchLng])

  const loadCompanies = useCallback(async (search?: string) => {
    try {
      const list = await getCompaniesForAddProperty(search)
      setCompanies(list as SearchableSelectOption[])
      if (!search && list.length === 1) {
        setValue('fk_company_id', list[0].value)
      }
    } catch (e) {
      console.error(e)
    }
  }, [setValue])

  const scrollToFirstError = () => {
    setTimeout(() => {
      const firstError = document.querySelector(
        '[data-testid="property-add-form"] [data-testid^="property-add-field-error-"], [data-testid="property-add-form"] .border-red-500',
      )
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
  }

  const handleValidateAddress = async () => {
    const ok = await trigger([
      'property_address',
      'property_city',
      'property_state',
      'property_zipcode',
    ])
    if (!ok) {
      scrollToFirstError()
      return
    }

    const data = watch()
    let address = data.property_address || ''
    if (data.property_address2) address += ' ' + data.property_address2
    address += ` ${data.property_city} ${data.property_state} ${data.property_zipcode}`

    setIsValidatingAddress(true)
    setMapMessage('')

    try {
      const { result: place, status } = await geocodeAddressRaw(address)

      // Angular: validateAddress = false UNCONDITIONALLY after any geocode attempt
      setAddressValidated(true)

      if (status && place) {
        if (!place.geometry || place.partial_match) {
          setMapMessage(ADDRESS_WARNING)
          setMessagePopup({ isOpen: true, title: 'Validation Message', message: ADDRESS_WARNING })
          return
        }

        const { zipValid, stateValid } = matchZipAndStateAngularStyle(
          place,
          data.property_zipcode,
          data.property_state
        )

        if (!zipValid || !stateValid) {
          setMapMessage(ADDRESS_WARNING)
          setMessagePopup({ isOpen: true, title: 'Validation Message', message: ADDRESS_WARNING })
          return
        }

        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        setValue('latitude', String(lat))
        setValue('longitude', String(lng))
        setMapMessage('')
        setMessagePopup({
          isOpen: true,
          title: 'Address Verified',
          message: `Address verified with Latitude: ${lat} Longitude: ${lng}`,
        })

        if (mapInstance.current && markerInstance.current) {
          const bounds = new google.maps.LatLngBounds()
          bounds.extend(place.geometry.location)
          mapInstance.current.fitBounds(bounds)
          markerInstance.current.setPosition({ lat, lng })
          mapInstance.current.setZoom(16)
        }
        trackApplicationEvent('map_refreshed', 'property')
      } else {
        setMapMessage(ADDRESS_WARNING)
        setMessagePopup({ isOpen: true, title: 'Validation Message', message: ADDRESS_WARNING })
      }
    } catch {
      setAddressValidated(true)
      setMapMessage(ADDRESS_WARNING)
      setMessagePopup({ isOpen: true, title: 'Validation Message', message: ADDRESS_WARNING })
    } finally {
      setIsValidatingAddress(false)
    }
  }

  const doCreate = async (saveAddress: boolean) => {
    const data = watch()
    const payload: Record<string, unknown> = {
      fk_company_id: data.fk_company_id,
      property_name: (data.property_name || '').trim(),
      contact_name: (data.contact_name || '').trim(),
      contact_phone: stripPhoneDigits(data.contact_phone),
      property_office_phone: stripPhoneDigits(data.property_office_phone),
      property_address: (data.property_address || '').trim(),
      property_address2: (data.property_address2 || '').trim(),
      property_city: (data.property_city || '').trim(),
      property_state: data.property_state,
      property_zipcode: (data.property_zipcode || '').trim(),
      units_count: data.units_count,
      listed: data.listed,
      listed_ids: [] as const,
      latitude: data.latitude ?? '',
      longitude: data.longitude ?? '',
    }
    const filtered: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(payload)) {
      if (value !== '' && value !== undefined && value !== null) {
        filtered[key] = value
      } else if (['fk_company_id', 'listed', 'units_count', 'latitude', 'longitude'].includes(key)) {
        filtered[key] = value
      }
    }
    const formData = buildPropertyFormData(filtered, { saveAddress })
    await createProperty(formData)
    trackApplicationEvent('property_added', 'property')
    showSuccess('Property Added Successfully!')
    navigate('/property')
  }

  const onSubmit = async (_data: PropertyAddFormValues) => {
    if (!addressValidated) {
      setMessagePopup({
        isOpen: true,
        title: 'Validation Message',
        message: 'Please Validate Address before Saving',
      })
      return
    }
    setIsSubmitting(true)
    try {
      await doCreate(false)
    } catch (err: unknown) {
      const existingProperties = parseDuplicateAddressError(err)
      if (existingProperties) {
        let htmlLinks = ''
        existingProperties.forEach((d) => {
          htmlLinks += `${d.name} - <a href="/property/edit/${d.id}" target="_blank">${d.id}</a><br>`
        })
        htmlLinks += '<br>If you have confirmed that there are multiple properties at the same address, then Save and continue.'
        setDuplicateAddressConfirm({ isOpen: true, propertyLinks: htmlLinks })
      } else {
        const errorMsg = formatApiError(err)
        setMessagePopup({
          isOpen: true,
          title: 'Error',
          message: errorMsg,
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDuplicateConfirm = async () => {
    setDuplicateAddressConfirm({ isOpen: false, propertyLinks: '' })
    setIsSubmitting(true)
    try {
      await doCreate(true)
    } catch (err) {
      const errorMsg = formatApiError(err)
      setMessagePopup({
        isOpen: true,
        title: 'Error',
        message: errorMsg,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full min-h-screen bg-slate-50/50" data-testid="property-add-page">
      <PageHeader title="Add Property" testIdRoot="property-add-page-header" />

      <div className="mx-4 my-4 sm:mx-6 sm:my-4">
        <h1 className="mb-4 text-center text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
          Tell us about your property
        </h1>

        <form
          data-testid="property-add-form"
          onSubmit={handleSubmit(onSubmit, (formErrors: FieldErrors<PropertyAddFormValues>) => {
            scrollToFirstError()
            const messages = collectNestedErrorMessages(formErrors)
            setMessagePopup({
              isOpen: true,
              title: 'Missing Fields',
              message:
                messages.length > 0
                  ? messages.join('\n')
                  : 'Please fix the validation errors before submitting.',
              variant: 'error',
              alignMessage: true,
              okButtonStyle: 'saveChanges',
            })
          })}
          className={cn(tabStyles.tabContent, 'form_inline w-full min-w-0')}
        >
          <SectionCard
            icon={Home}
            title="Property Information"
            testId="property-add-info-section"
            titleTestId="property-add-info-section-title"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Select Company<span className={styles.requiredStar}>*</span>
                </label>
                <Controller
                  name="fk_company_id"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      placeholder="All Company"
                      options={companies}
                      value={field.value ? String(field.value) : ''}
                      onBlur={field.onBlur}
                      onChange={(v) => {
                        if (!isCompanyReadonly) {
                          field.onChange(Array.isArray(v) ? v[0] : v)
                        }
                      }}
                      onSearch={isCompanyReadonly ? undefined : (s) => loadCompanies(s)}
                      disabled={isCompanyReadonly}
                      triggerDataTestId="property-add-company-select"
                      className={styles.customFormControl}
                    />
                  )}
                />
                {errors.fk_company_id && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-fk-company-id">
                    <AlertCircle className="h-4 w-4" />
                    {errors.fk_company_id.message}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Property Name<span className={styles.requiredStar}>*</span>
                </label>
                <Controller
                  name="property_name"
                  control={control}
                  render={({ field }) => (
                    <input
                      data-testid="property-add-property-name-input"
                      type="text"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        field.onChange(toTitleCase(e.target.value))
                      }}
                      onBlur={field.onBlur}
                      className={`${styles.customFormControl} ${errors.property_name ? '!border-red-500' : ''}`}
                    />
                  )}
                />
                {errors.property_name && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-property-name">
                    <AlertCircle className="h-4 w-4" />
                    {errors.property_name.message}
                  </div>
                )}
              </div>

              <div className={cn(styles.formGroup, 'min-w-0')}>
                <label className={styles.formLabel}>Office Phone</label>
                <Controller
                  name="property_office_phone"
                  control={control}
                  render={({ field }) => (
                    <input
                      data-testid="property-add-office-phone-input"
                      type="tel"
                      inputMode="tel"
                      maxLength={PROPERTY_PHONE_INPUT_MAX_LENGTH}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        field.onChange(maskPhoneOnChange(e.target.value))
                      }}
                      onBlur={field.onBlur}
                      className={`${styles.customFormControl} min-h-[2.375rem] ${errors.property_office_phone ? '!border-red-500' : ''}`}
                      placeholder="(XXX) XXX-XXXX"
                    />
                  )}
                />
                {errors.property_office_phone && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-office-phone">
                    <AlertCircle className="h-4 w-4" />
                    Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Primary Contact<span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  {...register('contact_name')}
                  data-testid="property-add-contact-name-input"
                  className={`${styles.customFormControl} ${errors.contact_name ? '!border-red-500' : ''}`}
                />
                {errors.contact_name && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-contact-name">
                    <AlertCircle className="h-4 w-4" />
                    {errors.contact_name.message}
                  </div>
                )}
              </div>

              <div className={cn(styles.formGroup, 'min-w-0')}>
                <label className={styles.formLabel}>
                  Primary Phone<span className={styles.requiredStar}>*</span>
                </label>
                <Controller
                  name="contact_phone"
                  control={control}
                  render={({ field }) => (
                    <input
                      data-testid="property-add-contact-phone-input"
                      type="tel"
                      inputMode="tel"
                      maxLength={PROPERTY_PHONE_INPUT_MAX_LENGTH}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        field.onChange(maskPhoneOnChange(e.target.value))
                      }}
                      onBlur={field.onBlur}
                      className={`${styles.customFormControl} min-h-[2.375rem] ${errors.contact_phone ? '!border-red-500' : ''}`}
                      placeholder="(XXX) XXX-XXXX"
                    />
                  )}
                />
                {errors.contact_phone && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-contact-phone">
                    <AlertCircle className="h-4 w-4" />
                    {errors.contact_phone.message}
                  </div>
                )}
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={MapPin}
            title="Address Information"
            testId="property-add-address-section"
            titleTestId="property-add-address-section-title"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Address Line1 <span className={styles.requiredStar}>*</span>
                </label>
                <textarea
                  {...register('property_address')}
                  data-testid="property-add-address-line1-input"
                  rows={2}
                  className={`${styles.customFormControl} min-h-[72px] resize-y ${errors.property_address ? '!border-red-500' : ''}`}
                />
                {errors.property_address && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-address-line1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.property_address.message}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Address Line2</label>
                <textarea
                  {...register('property_address2')}
                  data-testid="property-add-address-line2-input"
                  rows={2}
                  className={`${styles.customFormControl} min-h-[72px] resize-y`}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  City <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  {...register('property_city')}
                  data-testid="property-add-city-input"
                  className={`${styles.customFormControl} ${errors.property_city ? '!border-red-500' : ''}`}
                />
                {errors.property_city && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-city">
                    <AlertCircle className="h-4 w-4" />
                    {errors.property_city.message}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  State <span className={styles.requiredStar}>*</span>
                </label>
                <select
                  {...register('property_state')}
                  data-testid="property-add-state-select"
                  className={`${styles.customFormControl} ${errors.property_state ? '!border-red-500' : ''}`}
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.state_code} value={s.state_code}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {errors.property_state && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-state">
                    <AlertCircle className="h-4 w-4" />
                    {errors.property_state.message}
                  </div>
                )}
              </div>

              <div className={cn(styles.formGroup, 'min-w-0')}>
                <label className={styles.formLabel}>
                  Zipcode <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  {...register('property_zipcode')}
                  data-testid="property-add-zipcode-input"
                  className={`${styles.customFormControl} min-h-[2.375rem] ${errors.property_zipcode ? '!border-red-500' : ''}`}
                />
                {errors.property_zipcode && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-zipcode">
                    <AlertCircle className="h-4 w-4" />
                    {errors.property_zipcode.message}
                  </div>
                )}
              </div>
            </div>

            <div className="pt-1">
              <button
                type="button"
                onClick={handleValidateAddress}
                disabled={isValidatingAddress}
                data-testid="property-add-validate-address-button"
                className={cn(primaryEntityActionButtonClass, 'min-w-[150px] gap-2')}
              >
                {isValidatingAddress && <Loader2 className="h-4 w-4 animate-spin" />}
                Validate Address
              </button>
            </div>
            {mapMessage && (
              <div data-testid="property-add-map-message" className={styles.alertYlw} style={{ marginBottom: '0' }}>
                {mapMessage}
              </div>
            )}
          </SectionCard>

          <SectionCard
            icon={BadgeCheck}
            title="Liberty Reference"
            testId="property-add-liberty-ref-section"
            titleTestId="property-add-liberty-ref-section-title"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div
                className={cn(styles.formGroup, 'min-w-0')}
                style={{ opacity: !isLibertyRole ? 0.5 : 1, pointerEvents: !isLibertyRole ? 'none' : 'auto' }}
              >
                <label className={styles.formLabel}>
                  Active?<span className={styles.requiredStar}>*</span>
                </label>
                <select
                  {...register('listed')}
                  data-testid="property-add-listed-select"
                  className={`${styles.customFormControl} min-h-[2.375rem] ${errors.listed ? '!border-red-500' : ''}`}
                >
                  <option value="">Select Reference</option>
                  <option value="1">Active</option>
                  <option value="0">Not Active</option>
                </select>
                {errors.listed && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-listed">
                    <AlertCircle className="h-4 w-4" />
                    Liberty Reference Selection is required.
                  </div>
                )}
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={Building2}
            title="Public Information"
            testId="property-add-public-info-section"
            titleTestId="property-add-public-info-section-title"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className={cn(styles.formGroup, 'min-w-0')}>
                <label className={styles.formLabel}>
                  # of Units<span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  {...register('units_count')}
                  data-testid="property-add-units-count-input"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={`${styles.customFormControl} min-h-[2.375rem] ${errors.units_count ? '!border-red-500' : ''}`}
                  placeholder="0"
                />
                {errors.units_count && (
                  <div className={styles.errorMessage} data-testid="property-add-field-error-units-count">
                    <AlertCircle className="h-4 w-4" />
                    {errors.units_count.message}
                  </div>
                )}
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon={Map}
            title="Location Map"
            testId="property-add-location-map-section"
            titleTestId="property-add-location-map-section-title"
          >
            <input type="hidden" {...register('latitude')} data-testid="property-add-latitude-input" />
            <input type="hidden" {...register('longitude')} data-testid="property-add-longitude-input" />
            <div>
              <button
                type="button"
                data-testid="property-add-refresh-map-button"
                className={cn(primaryEntityActionButtonClass, 'min-w-[150px] gap-2')}
                onClick={() => {
                  const lat = Number(watch('latitude')) || 30.5557898
                  const lng = Number(watch('longitude')) || -87.8939053
                  if (mapInstance.current) {
                    mapInstance.current.setCenter({ lat, lng })
                    markerInstance.current?.setPosition({ lat, lng })
                    mapInstance.current.setZoom(16)
                    window.google?.maps?.event?.trigger(mapInstance.current, 'resize')
                  }
                  trackApplicationEvent('map_refreshed', 'property')
                }}
              >
                Refresh Map
              </button>
            </div>
            <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
              <div
                ref={mapRef}
                data-testid="property-add-map"
                className="h-[300px] w-full bg-slate-200"
              />
            </div>
          </SectionCard>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="property-add-submit-button"
              className={cn(primaryEntityActionButtonClass, 'min-w-[150px] gap-2')}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit
            </button>
            <button
              type="button"
              onClick={() => navigate('/property')}
              disabled={isSubmitting}
              data-testid="property-add-cancel-button"
              className={cn(styles.applyButton, styles.cancelButtonSecondary, 'min-w-[150px]')}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <ValidationModal
        isOpen={messagePopup.isOpen}
        title={messagePopup.title}
        message={messagePopup.message}
        variant={messagePopup.variant ?? 'error'}
        onClose={() => setMessagePopup({ isOpen: false, title: '', message: '' })}
        onConfirm={() => setMessagePopup({ isOpen: false, title: '', message: '' })}
        showValidationIcon={
          messagePopup.title === 'Validation Message' ||
          messagePopup.title === 'Missing Fields' ||
          messagePopup.title === 'Validate Address'
        }
        alignMessage={messagePopup.alignMessage}
        okButtonStyle={messagePopup.okButtonStyle}
        overlayDataTestId="property-add-validation-modal-overlay"
        closeButtonDataTestId="property-add-validation-modal-close-button"
        confirmButtonDataTestId="property-add-validation-modal-ok-button"
      />

      <ConfirmDialog
        isOpen={duplicateAddressConfirm.isOpen}
        title="Another property exists at this address"
        message=""
        htmlMessage={duplicateAddressConfirm.propertyLinks}
        primaryLabel="Save"
        onPrimary={handleDuplicateConfirm}
        secondaryLabel="Cancel"
        onSecondary={() =>
          setDuplicateAddressConfirm({ isOpen: false, propertyLinks: '' })
        }
        presentation="centeredPillYesNo"
        variant="warning"
        overlayDataTestId="property-add-duplicate-address-confirm-overlay"
        primaryButtonDataTestId="property-add-duplicate-address-confirm-save-button"
        secondaryButtonDataTestId="property-add-duplicate-address-confirm-cancel-button"
        closeButtonDataTestId="property-add-duplicate-address-confirm-close-button"
      />
    </div>
  )
}
